import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Car, 
  Users, 
  Shield, 
  Clock,
  MapPin,
  Wifi,
  Star,
  CheckCircle,
  Plane,
  Crown,
  ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";
import { useVehicleImages } from "@/hooks/useVehicleImages";
import transfersHeroBg from "@/assets/transfers-hero-bg.jpg";
import GoldenParticles from "@/components/GoldenParticles";
import WaveGoldDivider from "@/components/WaveGoldDivider";
import VIPBadge from "@/components/VIPBadge";
import AnimatedSection from "@/components/AnimatedSection";

const Transfers = () => {
  const { vehicleImages, loading } = useVehicleImages();

  const transferTypes = [
    {
      title: "VIP Airport Transfers",
      description: "Premium pickup and drop-off service from/to Punta Cana International Airport",
      icon: Plane,
      routes: ["Airport to Bavaro/Punta Cana Hotels", "Airport to Cap Cana Resorts", "Airport to Uvero Alto Hotels", "Airport to La Romana"]
    },
    {
      title: "Luxury Hotel Transfers",
      description: "First-class transportation between hotels and destinations",
      icon: MapPin,
      routes: ["Hotel to Hotel Transfers", "Hotel to Shopping Centers", "Hotel to Fine Dining", "Hotel to Attractions"]
    },
    {
      title: "Private VIP Transportation",
      description: "Dedicated luxury vehicle and driver for your exclusive use",
      icon: Car,
      routes: ["Custom Itineraries", "Multiple Destinations", "Wait Time Included", "Flexible Scheduling"]
    },
    {
      title: "Executive Group Transportation",
      description: "Specialized VIP service for large groups and corporate events",
      icon: Users,
      routes: ["Wedding Groups", "Corporate Events", "Family Reunions", "Tour Groups"]
    }
  ];

  const features = [
    { icon: Shield, title: "Safety First", description: "All vehicles regularly inspected and fully insured" },
    { icon: Clock, title: "On-Time VIP Service", description: "Punctual pickup and delivery with flight monitoring" },
    { icon: Users, title: "Professional Drivers", description: "Licensed, experienced VIP chauffeurs with local knowledge" },
    { icon: Wifi, title: "Luxury Amenities", description: "Air conditioning, premium seating, and complimentary refreshments" }
  ];

  const popularRoutes = [
    { route: "Airport ↔ Bavaro/Punta Cana", time: "45 minutes", distance: "25 km", price: "From $45" },
    { route: "Airport ↔ Cap Cana", time: "30 minutes", distance: "15 km", price: "From $55" },
    { route: "Airport ↔ Uvero Alto", time: "60 minutes", distance: "35 km", price: "From $65" },
    { route: "Airport ↔ La Romana", time: "90 minutes", distance: "60 km", price: "From $85" }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section 
        className="relative min-h-[70vh] flex items-center justify-center text-white bg-cover bg-center bg-no-repeat overflow-hidden"
        style={{ backgroundImage: `url(${transfersHeroBg})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/70 to-primary/30"></div>
        <GoldenParticles count={20} />
        
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection className="max-w-4xl mx-auto text-center">
            <VIPBadge variant="exclusive" size="lg" icon="crown" className="mb-6" animated>
              VIP Transfer Services
            </VIPBadge>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
              <span className="text-gradient-gold">Premium</span> Transportation
              <br />
              <span className="text-white">in Punta Cana</span>
            </h1>
            <p className="text-xl text-white/80 leading-relaxed max-w-3xl mx-auto">
              Professional VIP transfer services for individuals, families, and groups. 
              From airport pickups to custom luxury transportation solutions, we ensure 
              your journey is comfortable, safe, and unforgettable.
            </p>
          </AnimatedSection>
        </div>
        
        <WaveGoldDivider className="absolute bottom-0 left-0 right-0" />
      </section>

      {/* Vehicle Fleet */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <VIPBadge variant="gold" className="mb-4">Our Luxury Fleet</VIPBadge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gradient-gold">
              Choose Your Perfect Vehicle
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From intimate couples' transfers to large group VIP transportation, 
              we have the perfect luxury vehicle for every need
            </p>
          </AnimatedSection>

          {loading ? (
            <div className="text-center py-12">
              <Crown className="w-8 h-8 mx-auto animate-pulse-glow text-primary mb-4" />
              <p className="text-muted-foreground">Loading VIP fleet...</p>
            </div>
          ) : vehicleImages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No vehicles available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {vehicleImages
                .sort((a, b) => a.min_passengers - b.min_passengers)
                .map((vehicle, index) => (
                <AnimatedSection key={vehicle.id} delay={index * 100}>
                  <Card className={`text-center hover:shadow-gold transition-all duration-300 hover:-translate-y-2 border-transparent hover:border-primary/30 relative ${index === 1 ? 'ring-2 ring-primary shadow-gold' : ''}`}>
                    {index === 1 && (
                      <VIPBadge variant="exclusive" size="sm" className="absolute -top-3 left-1/2 -translate-x-1/2">
                        Most Popular
                      </VIPBadge>
                    )}
                    <CardHeader>
                      <div 
                        className="w-full h-32 bg-cover bg-center bg-no-repeat rounded-lg mb-4"
                        style={{ backgroundImage: `url(${vehicle.image_url})` }}
                      >
                        <div className="w-full h-full bg-gradient-to-t from-black/40 to-transparent rounded-lg"></div>
                      </div>
                      <CardTitle className="text-xl">{vehicle.vehicle_name}</CardTitle>
                      <Badge className="mt-2 bg-gradient-gold text-white border-0">
                        {vehicle.service_type === 'standard' ? 'Premium Service' : 'VIP Exclusive'}
                      </Badge>
                      <CardDescription className="mt-2">
                        {vehicle.description || 'Professional VIP transfer service'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 mb-6">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Capacity:</span>
                          <span className="font-medium">{vehicle.min_passengers}-{vehicle.max_passengers} guests</span>
                        </div>
                      </div>
                      <div className="space-y-2 mb-6">
                        {vehicle.features && vehicle.features.length > 0 ? (
                          vehicle.features.map((feature, featureIndex) => (
                            <div key={featureIndex} className="flex items-center space-x-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                              <span className="text-muted-foreground">{feature}</span>
                            </div>
                          ))
                        ) : (
                          <div className="flex items-center space-x-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="text-muted-foreground">VIP Professional Service</span>
                          </div>
                        )}
                      </div>
                      <Link to="/">
                        <Button className={`w-full ${index === 1 ? 'bg-gradient-gold shadow-gold' : 'bg-gradient-primary'} text-white hover:opacity-90 transition-all duration-300`}>
                          Book Now
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Transfer Types */}
      <section className="py-24 bg-vip-dark relative overflow-hidden">
        <GoldenParticles count={15} />
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection className="text-center mb-16">
            <VIPBadge variant="premium" className="mb-4">Service Types</VIPBadge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Comprehensive <span className="text-gradient-gold">VIP Solutions</span>
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Whether you need a simple airport pickup or complex multi-destination 
              transportation, we have the perfect VIP solution for you
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {transferTypes.map((type, index) => (
              <AnimatedSection key={index} delay={index * 100}>
                <Card className="bg-white/5 border-primary/30 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:-translate-y-2 hover:shadow-gold">
                  <CardHeader>
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-14 h-14 bg-gradient-gold rounded-full flex items-center justify-center">
                        <type.icon className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-white">{type.title}</CardTitle>
                        <CardDescription className="text-white/70 text-base">
                          {type.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2">
                      {type.routes.map((route, routeIndex) => (
                        <div key={routeIndex} className="flex items-center space-x-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                          <span className="text-white/80">{route}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Routes */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <VIPBadge variant="gold" className="mb-4">Popular Routes</VIPBadge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gradient-gold">
              Most Requested VIP Transfers
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our most popular transfer destinations with estimated times and distances
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularRoutes.map((route, index) => (
              <AnimatedSection key={index} delay={index * 100}>
                <Card className="text-center hover:shadow-gold transition-all duration-300 hover:-translate-y-2 border-transparent hover:border-primary/30">
                  <CardHeader>
                    <CardTitle className="text-lg">{route.route}</CardTitle>
                    <div className="text-3xl font-bold text-gradient-gold">{route.price}</div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Duration:</span>
                        <span className="font-medium">{route.time}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Distance:</span>
                        <span className="font-medium">{route.distance}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <VIPBadge variant="gold" className="mb-4">Why Choose Our VIP Transfers</VIPBadge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gradient-gold">
              Premium Service Standards
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Every aspect of our service is designed to provide you with the 
              best possible VIP transportation experience
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <AnimatedSection key={index} delay={index * 100}>
                <Card className="text-center hover:shadow-gold transition-all duration-300 hover:-translate-y-2 border-transparent hover:border-primary/30">
                  <CardHeader>
                    <div className="mx-auto w-16 h-16 bg-gradient-gold rounded-full flex items-center justify-center mb-4">
                      <feature.icon className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-24 bg-vip-dark relative overflow-hidden">
        <GoldenParticles count={20} />
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection className="max-w-4xl mx-auto">
            <div className="text-center">
              <Crown className="h-16 w-16 mx-auto mb-6 text-primary animate-float" />
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Book Your <span className="text-gradient-gold">VIP Transfer</span> Today
              </h2>
              <p className="text-xl text-white/80 mb-8 leading-relaxed">
                Secure, comfortable, and reliable VIP transportation is just a booking away. 
                Contact us for instant quotes and reservations.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="flex items-center justify-center space-x-3 text-white/80">
                  <Clock className="h-6 w-6 text-primary" />
                  <span>24/7 Availability</span>
                </div>
                <div className="flex items-center justify-center space-x-3 text-white/80">
                  <Shield className="h-6 w-6 text-primary" />
                  <span>Fully Insured</span>
                </div>
                <div className="flex items-center justify-center space-x-3 text-white/80">
                  <Star className="h-6 w-6 text-primary" />
                  <span>5-Star VIP Service</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button size="lg" className="bg-gradient-gold text-white hover:opacity-90 shadow-gold text-lg px-8 py-6 group">
                    Get Instant Quote
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Button variant="outline" size="lg" className="border-primary/50 text-white hover:bg-primary/20 text-lg px-8 py-6">
                  Call Now: +1 (809) 123-4567
                </Button>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default Transfers;
